/**
 * OmegaBrainPage — L2 of the OMEGA zoom (`/omega/brain/:brainId`).
 *
 * Uses live API data from useBrainSummaries() — no hardcoded registry.
 * Shows bound specialists, live stats, and triggers for any brain
 * regardless of status.
 */

import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { getBrain } from '@/features/omega/dashboard/data/brainRegistry';
import { useBrainSummaries } from '@/features/omega/dashboard/hooks/useOmegaState';
import type { BrainSummary } from '@/features/omega/dashboard/data/types';
import { cn } from '@/utils/cn';
import { apiFetch } from '@/api/http';
import { getOmegaLogTail } from '@/api/real/omegaLog';
import { listTriggers, fireTrigger, setTriggerEnabled } from '@/api/real/triggers';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { canonicalJsonStringify } from '@/api/mock/sign';
import type { OmegaLogEntry, TriggerSummary } from '@/api/types';

export function OmegaBrainPage() {
  const { brainId } = useParams<{ brainId: string }>();

  const v2Enabled = import.meta.env.VITE_BRAIN_DETAIL_V2_ENABLED !== 'false';

  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  const { data: brains, isPending, isError } = useBrainSummaries();
  const registryEntry = getBrain(brainId ?? '');

  // Log tail for v2 brain-specific section — fetched once at page level.
  const { data: logEntries } = useQuery({
    queryKey: ['omega', 'log', 'tail', 'recent'],
    queryFn: () => getOmegaLogTail(undefined, 100),
    refetchInterval: 30_000,
    select: (d) => d.entries,
    enabled: v2Enabled,
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          crumbs={[
            { label: 'OMEGA', to: '/omega' },
            { label: registryEntry?.display_name ?? brainId ?? '…' },
          ]}
        />
        <p className="text-sm text-ink-muted">Loading…</p>
      </div>
    );
  }

  if (isError || !brains) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          crumbs={[
            { label: 'OMEGA', to: '/omega' },
            { label: registryEntry?.display_name ?? brainId ?? 'Brain' },
          ]}
        />
        <p className="text-sm text-danger">Failed to load brain data.</p>
      </div>
    );
  }

  const brain = brains.find((b) => b.id === brainId);

  if (!brain) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          crumbs={[
            { label: 'OMEGA', to: '/omega' },
            { label: 'Unknown brain' },
          ]}
        />
        <p className="text-sm text-ink-muted">
          No brain registered with id <span className="font-mono">{brainId}</span>.
        </p>
      </div>
    );
  }

  const displayName = registryEntry?.display_name ?? brain.id;
  const purpose = registryEntry?.purpose ?? '';

  if (!v2Enabled) {
    return (
      <div className="flex flex-col gap-6">
        <Breadcrumbs
          crumbs={[
            { label: 'OMEGA', to: '/omega' },
            { label: displayName },
          ]}
        />

        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{displayName}</h1>
            <BrainStatusBadge status={brain.status} />
          </div>
          {purpose && <p className="text-sm text-ink-muted">{purpose}</p>}
        </header>

        <StatsBar brain={brain} />

        <SpecialistList brain={brain} />

        {(brain.bound_triggers ?? []).length > 0 && <TriggerList triggers={brain.bound_triggers ?? []} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: displayName },
        ]}
      />

      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{displayName}</h1>
          <BrainStatusBadge status={brain.status} />
        </div>
        {purpose && <p className="text-sm text-ink-muted">{purpose}</p>}
      </header>

      <StatsBar brain={brain} />

      <RecentExecutionsSection
        brainId={brain.id}
        executions24h={brain.executions_last_24h ?? 0}
      />

      <EnhancedSpecialistsSection brain={brain} />

      <TriggersPanel specialistIds={(brain.bound_specialists ?? []).map((s) => s.id)} />

      <BrainSpecificSection brainId={brain.id} logEntries={logEntries ?? []} />
    </div>
  );
}

// ─── StatsBar ─────────────────────────────────────────────────────

function StatsBar({ brain }: { brain: BrainSummary }) {
  const memoryMb = brain.resource_usage_mb_current;
  const items = [
    { label: 'Organs', value: brain.organ_count ?? '—', tooltip: 'Number of bound specialists in this brain' },
    { label: 'Workers', value: brain.worker_count?.toLocaleString() ?? '—', tooltip: 'Live runtime workers (placeholder until WASM workers are tracked)' },
    { label: 'Decisions / 1m', value: brain.decisions_last_min ?? '—', tooltip: 'Decisions per minute, rolling 60-second window (placeholder)' },
    { label: 'Refusals / 1m', value: brain.refusals_last_min ?? '—', tooltip: 'Refused executions per minute (placeholder)' },
    { label: 'Executions / 24h', value: brain.executions_last_24h ?? '—', tooltip: 'Completed specialist executions in the last 24 hours' },
    { label: 'Memory MB', value: typeof memoryMb === 'number' && memoryMb > 0 ? memoryMb : '—', tooltip: 'Configured memory limit per WASM specialist (MB)' },
  ];
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 rounded-xl border border-border bg-surface p-4">
      {items.map(({ label, value, tooltip }) => (
        <div key={label} title={tooltip} className="flex flex-col gap-1 cursor-help">
          <span className="text-[11px] uppercase tracking-wider text-ink-subtle">{label}</span>
          <span className="font-mono text-base font-semibold text-ink">{value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── SpecialistList (preserved) ───────────────────────────────────

function SpecialistList({ brain }: { brain: BrainSummary }) {
  const specialists = brain.bound_specialists ?? [];
  if (specialists.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface p-6">
        <h2 className="text-lg font-semibold text-ink">Specialists</h2>
        <p className="mt-1 text-sm text-ink-muted">
          No specialists bound to this brain yet.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="brain-specialists-heading"
      className="rounded-xl border border-border bg-surface p-6"
    >
      <h2 id="brain-specialists-heading" className="text-lg font-semibold text-ink">
        Specialists
      </h2>
      <p className="mt-1 text-sm text-ink-muted">
        {specialists.length} specialist
        {specialists.length === 1 ? '' : 's'} bound to {brain.id}.
      </p>

      <div className="mt-5 overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
              <th className="px-3 py-2 text-start font-medium">ID</th>
              <th className="px-3 py-2 text-start font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {specialists.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-surface-raised/50">
                <td className="px-3 py-2 font-medium text-ink">
                  <Link
                    to={`/omega/brain/${brain.id}/${s.id}`}
                    className="hover:text-spark hover:underline"
                  >
                    {s.id}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <SpecialistStatusChip status={s.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── TriggerList (preserved) ──────────────────────────────────────

function TriggerList({ triggers }: { triggers: string[] }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <h2 className="text-lg font-semibold text-ink">Triggers</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {triggers.map((t) => (
          <Link
            key={t}
            to="/omega/triggers"
            className="rounded border border-border px-2 py-1 font-mono text-xs text-ink-muted hover:text-ink hover:border-spark"
          >
            {t}
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── BrainStatusBadge (preserved) ────────────────────────────────

function BrainStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'active' || status === 'healthy'
      ? 'border-success/30 bg-success-soft text-success'
      : 'border-border bg-surface-raised text-ink-muted';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ─── SpecialistStatusChip (preserved) ────────────────────────────

function SpecialistStatusChip({ status }: { status: string }) {
  const tone =
    status === 'live'
      ? 'border-success/30 bg-success-soft text-success'
      : status === 'error'
        ? 'border-danger/30 bg-danger/10 text-danger'
        : 'border-border bg-surface-raised text-ink-muted';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ─── v2: ExecDrawer ───────────────────────────────────────────────

function ExecDrawer({ entry, onClose }: { entry: OmegaLogEntry; onClose: () => void }) {
  const payloadStr = JSON.stringify(entry.payload_json, null, 2);
  const sigPrefix = entry.signature.slice(0, 40);

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-label="Execution detail"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* panel */}
      <div
        className="absolute end-0 top-0 bottom-0 overflow-y-auto p-6 bg-surface border-s border-border"
        style={{ width: 'min(480px, 100vw)' }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-ink">Execution Detail</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Entry Type</span>
            <span className="ltr-content font-mono text-xs text-ink">{entry.entry_type}</span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Sequence</span>
            <span className="font-mono text-xs text-ink">{entry.seq}</span>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Payload JSON</span>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(payloadStr)}
                className="rounded border border-border px-2 py-0.5 text-[10px] text-ink-muted hover:text-ink"
              >
                Copy
              </button>
            </div>
            <pre className="ltr-content max-h-64 overflow-auto rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink-muted whitespace-pre-wrap break-all">
              {payloadStr}
            </pre>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Signature (prefix)</span>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(entry.signature)}
                className="rounded border border-border px-2 py-0.5 text-[10px] text-ink-muted hover:text-ink"
              >
                Copy full
              </button>
            </div>
            <span className="ltr-content font-mono text-xs text-ink-muted break-all">
              {sigPrefix}…
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── v2: RecentExecutionsSection ─────────────────────────────────

/** Format a Unix-seconds timestamp as a short human local date-time. */
function formatExecWhen(timestampSecs: number | null): string {
  if (timestampSecs === null) return '—';
  return new Date(timestampSecs * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function RecentExecutionsSection({
  brainId,
  executions24h,
}: {
  brainId: string;
  executions24h: number;
}) {
  // Source the LIST from the same place as the header count: `brain_execution`
  // log entries (the entry type counted by `executions_last_24h`). The server
  // `brain_id` filter narrows the window to this brain's specialists; we then
  // keep only `brain_execution` entries so the rows are real executions, not
  // mixed log noise. This is why the count and the list now tell one story.
  const { data: executions = [], isLoading } = useQuery({
    queryKey: ['brain', 'executions', brainId],
    queryFn: () => getOmegaLogTail(undefined, 200, brainId),
    refetchInterval: 10_000,
    select: (data) =>
      data.entries
        .filter((e) => e.entry_type === 'brain_execution')
        .map((e) => {
          const p = (e.payload_json ?? {}) as Record<string, unknown>;
          return {
            seq: e.seq,
            specialistId:
              typeof p['specialist_id'] === 'string'
                ? (p['specialist_id'] as string)
                : 'unknown specialist',
            timestampSecs:
              typeof p['timestamp_secs'] === 'number'
                ? (p['timestamp_secs'] as number)
                : null,
          };
        })
        .sort((a, b) => b.seq - a.seq),
  });

  const countLabel = `${executions24h.toLocaleString()} execution${
    executions24h === 1 ? '' : 's'
  } in the last 24 hours`;

  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-ink">Recent Executions</h2>
        <span className="text-sm text-ink-muted">{countLabel}</span>
      </div>

      {isLoading && <p className="text-sm text-ink-muted">Loading executions…</p>}

      {!isLoading && executions.length === 0 && (
        <p className="text-sm text-ink-muted">
          {executions24h > 0
            ? `${countLabel}. The most recent runs will appear here as they happen.`
            : 'No specialist has run in this brain in the last 24 hours.'}
        </p>
      )}

      {executions.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                <th className="px-3 py-2 text-start font-medium">Specialist</th>
                <th className="px-3 py-2 text-start font-medium">What happened</th>
                <th className="px-3 py-2 text-end font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((x) => (
                <tr key={x.seq} className="border-t border-border hover:bg-surface-raised/50">
                  <td className="px-3 py-2 font-medium text-ink ltr-content">{x.specialistId}</td>
                  <td className="px-3 py-2 text-ink-muted">Ran successfully</td>
                  <td className="px-3 py-2 text-end text-ink-muted">{formatExecWhen(x.timestampSecs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── v2: EnhancedSpecialistsSection ──────────────────────────────

function EnhancedSpecialistsSection({ brain }: { brain: BrainSummary }) {
  const navigate = useNavigate();
  const specialists = brain.bound_specialists ?? [];

  if (specialists.length === 0) {
    return <SpecialistList brain={brain} />;
  }

  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center gap-2 mb-5">
        <h2 className="text-lg font-semibold text-ink">Specialists</h2>
        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-ink-muted">
          {specialists.length}
        </span>
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
              <th className="px-3 py-2 text-start font-medium">ID</th>
              <th className="px-3 py-2 text-start font-medium">Status</th>
              <th className="px-3 py-2 text-start font-medium">Last Exec</th>
              <th className="px-3 py-2 text-start font-medium">Execs Today</th>
              <th className="px-3 py-2 text-end font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {specialists.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-surface-raised/50">
                <td className="px-3 py-2 font-medium">
                  <Link
                    to={`/omega/specialist/${s.id}`}
                    className="ltr-content hover:text-spark hover:underline"
                  >
                    {s.id}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <SpecialistStatusChip status={s.status} />
                </td>
                <td className="px-3 py-2 text-ink-muted text-xs">—</td>
                <td className="px-3 py-2 text-ink-muted text-xs">—</td>
                <td className="px-3 py-2 text-end">
                  <button
                    onClick={() => navigate(`/omega/specialist/${s.id}`)}
                    className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:border-spark hover:text-spark"
                  >
                    Inspect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── v2: TriggerDisableDialog ─────────────────────────────────────

function TriggerDisableDialog({
  triggerId,
  isPending,
  error,
  onSign,
  onClose,
}: {
  triggerId: string;
  isPending: boolean;
  error?: string | undefined;
  onSign: (sig: string) => void;
  onClose: () => void;
}) {
  const [sig, setSig] = useState('');
  const [autoSignPending, setAutoSignPending] = useState(false);
  const [autoSignError, setAutoSignError] = useState('');

  const req = { trigger_id: triggerId, nonce: crypto.randomUUID() };
  const payload = canonicalJsonStringify(req);
  const payloadBytes = new TextEncoder().encode(payload);
  const hashHex = bytesToHex(sha256(payloadBytes));
  const hashString = `sha256:${hashHex}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Disable trigger"
    >
      <div className="flex w-full max-w-lg flex-col gap-5 rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">Disable Trigger</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink">
            ✕
          </button>
        </div>

        <p className="text-sm text-ink-muted">
          Sign the hash below with your founder Ed25519 key. Paste the result in the field at the
          bottom.
        </p>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Canonical payload (JCS JSON)
          </label>
          <pre className="max-h-32 overflow-auto rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink-muted ltr-content">
            {payload}
          </pre>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">Hash to sign</label>
          <div className="flex items-center gap-2 rounded-md border border-border bg-canvas px-3 py-2">
            <code className="flex-1 break-all font-mono text-[11px] text-ink ltr-content">
              {hashString}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(hashString)}
              className="shrink-0 rounded border border-border px-2 py-1 text-[10px] text-ink-muted hover:text-ink"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label htmlFor="disable-sig-input" className="text-[10px] uppercase tracking-wider text-ink-subtle">
              Founder signature
            </label>
            <button
              type="button"
              disabled={autoSignPending}
              onClick={() => {
                setAutoSignPending(true);
                setAutoSignError('');
                signHashWithFounderKey(hashHex)
                  .then((s) => {
                    setSig(s);
                    setAutoSignPending(false);
                  })
                  .catch((e: unknown) => {
                    setAutoSignError(e instanceof Error ? e.message : 'Sign failed');
                    setAutoSignPending(false);
                  });
              }}
              className="text-[10px] text-spark hover:underline disabled:opacity-50"
            >
              {autoSignPending ? 'Signing…' : 'Sign with my key ↗'}
            </button>
          </div>
          {autoSignError && <p className="text-[10px] text-danger">{autoSignError}</p>}
          <input
            id="disable-sig-input"
            type="text"
            placeholder="ed25519:BASE64_SIGNATURE"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            className={cn(
              'rounded-md border border-border bg-canvas px-3 py-2',
              'font-mono text-xs text-ink placeholder:text-ink-subtle',
              'focus:border-ink-muted focus:outline-none',
            )}
          />
          {error && <p className="text-xs text-danger">{error}</p>}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-ink-muted hover:bg-canvas"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!sig.trim() || isPending}
            onClick={() => onSign(sig.trim())}
            className={cn(
              'rounded-md border border-ink/30 bg-canvas px-4 py-2',
              'text-sm font-semibold text-ink transition-colors',
              'hover:border-ink hover:bg-surface',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── v2: TriggersPanel ────────────────────────────────────────────

function TriggersPanel({ specialistIds }: { specialistIds: string[] }) {
  const qc = useQueryClient();
  const [pendingDisable, setPendingDisable] = useState<string | null>(null);
  const [disableError, setDisableError] = useState('');

  const { data: allTriggers = [] } = useQuery({
    queryKey: ['triggers', 'list'],
    queryFn: listTriggers,
    refetchInterval: 30_000,
  });

  const triggers = allTriggers.filter((t) => specialistIds.includes(t.specialist_id));

  const fire = useMutation({
    mutationFn: (id: string) => fireTrigger(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['triggers', 'list'] }),
  });

  const disable = useMutation({
    mutationFn: ({ id, sig }: { id: string; sig: string }) => {
      const req = { trigger_id: id, nonce: crypto.randomUUID() };
      return setTriggerEnabled(id, req, false, sig);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triggers', 'list'] });
      setPendingDisable(null);
      setDisableError('');
    },
    onError: (e: unknown) => {
      setDisableError(e instanceof Error ? e.message : 'Disable failed');
    },
  });

  if (triggers.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface p-6">
        <h2 className="text-lg font-semibold text-ink">Triggers</h2>
        <p className="mt-1 text-sm text-ink-muted">
          No triggers bound to specialists in this brain.{' '}
          <Link to="/omega/triggers" className="text-spark hover:underline">
            Manage triggers
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center gap-2 mb-5">
        <h2 className="text-lg font-semibold text-ink">Triggers</h2>
        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-ink-muted">
          {triggers.length}
        </span>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
              <th className="px-3 py-2 text-start font-medium">Trigger ID</th>
              <th className="px-3 py-2 text-start font-medium">Type</th>
              <th className="px-3 py-2 text-start font-medium">Enabled</th>
              <th className="px-3 py-2 text-end font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {triggers.map((t) => (
              <tr key={t.trigger_id} className="border-t border-border hover:bg-surface-raised/50">
                <td className="px-3 py-2 font-mono text-xs text-ink ltr-content">
                  {t.trigger_id}
                </td>
                <td className="px-3 py-2">
                  <TriggerTypeBadge kind={t.trigger_type.kind} />
                </td>
                <td className="px-3 py-2">
                  <EnabledBadge enabled={t.enabled} />
                </td>
                <td className="px-3 py-2 text-end">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => fire.mutate(t.trigger_id)}
                      disabled={fire.isPending}
                      className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:border-spark hover:text-spark disabled:opacity-40"
                    >
                      Fire
                    </button>
                    {t.enabled && (
                      <button
                        onClick={() => {
                          setPendingDisable(t.trigger_id);
                          setDisableError('');
                        }}
                        className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:border-danger hover:text-danger"
                      >
                        Disable
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pendingDisable !== null && (
        <TriggerDisableDialog
          triggerId={pendingDisable}
          isPending={disable.isPending}
          error={disableError || undefined}
          onSign={(sig) => disable.mutate({ id: pendingDisable, sig })}
          onClose={() => {
            setPendingDisable(null);
            setDisableError('');
          }}
        />
      )}
    </section>
  );
}

function TriggerTypeBadge({ kind }: { kind: TriggerSummary['trigger_type']['kind'] }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-surface-raised px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
      {kind}
    </span>
  );
}

function DiscoveryUnitStateBadge({ state }: { state: DiscoveryUnitState }) {
  const isRunning = state === 'running';
  return (
    <span
      className={cn(
        'w-fit rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider',
        isRunning
          ? 'border-success/30 bg-success-soft text-success'
          : 'border-border bg-surface-raised text-ink-muted',
      )}
    >
      {isRunning ? 'Running' : 'Blueprint'}
    </span>
  );
}

function EnabledBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        enabled
          ? 'border-success/30 bg-success-soft text-success'
          : 'border-border bg-surface-raised text-ink-muted',
      )}
    >
      {enabled ? 'on' : 'off'}
    </span>
  );
}

// ─── v2: BrainSpecificSection ─────────────────────────────────────

interface DiscoveryLabStatus {
  name: string;
  last_run_iso: string | null;
  output_count: number;
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function DiscoveryUnitsSection() {
  const qc = useQueryClient();
  const { data: labStatuses } = useQuery({
    queryKey: ['omega', 'brain', 'discovery', 'status'],
    queryFn: () => apiFetch<DiscoveryLabStatus[]>('/omega/brain/discovery/status'),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const runNow = useMutation({
    mutationFn: () =>
      apiFetch<{ total_outputs: number }>('/omega/brain/discovery/run', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['omega', 'brain', 'discovery', 'status'] });
      void qc.invalidateQueries({ queryKey: ['omega', 'brain', 'discovery', 'outputs'] });
    },
  });


  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">Discovery Units</h2>
        <button
          type="button"
          onClick={() => runNow.mutate()}
          disabled={runNow.isPending}
          className="min-h-[40px] rounded-md border border-spark bg-spark/10 px-4 text-sm font-semibold text-spark transition-colors hover:bg-spark/20 disabled:opacity-60"
        >
          {runNow.isPending ? 'Running…' : 'Run Now'}
        </button>
      </div>
      {runNow.isError && (
        <p className="mb-3 text-xs text-red-400">Run failed — check your founder session.</p>
      )}
      {runNow.isSuccess && (
        <p className="mb-3 text-xs text-emerald-400">
          Cycle complete — {runNow.data?.total_outputs ?? 0} outputs produced.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {DISCOVERY_UNITS.map((unit) => {
          const status = labStatuses?.find((s) => s.name === unit.name);
          const lastRun = status?.last_run_iso
            ? new Date(status.last_run_iso).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—';
          // Real durable per-lab count from the status endpoint (count_by_lab in the
          // store) — not a filter over the 50 most-recent outputs, which under-counted.
          const persistedCount = status?.output_count ?? 0;
          return (
            <Link
              key={unit.name}
              to={`/omega/brain/discovery/${slugify(unit.name)}/outputs`}
              className={cn(
                'flex flex-col gap-2 rounded-lg border bg-canvas p-4 hover:border-spark transition-colors',
                unit.state === 'running' ? 'border-border' : 'border-dashed border-border',
              )}
            >
              <span className="text-sm font-medium text-ink">{unit.name}</span>
              <DiscoveryUnitStateBadge state={unit.state} />
              <span className="text-[11px] text-ink-subtle">Last run: {lastRun}</span>
              <span className="text-[11px] text-ink-subtle">
                Outputs: {persistedCount}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export type DiscoveryUnitState = 'running' | 'blueprint';

export interface DiscoveryUnitEntry {
  name: string;
  state: DiscoveryUnitState;
}

// State reflects discovery_loop.rs cron wiring. All six labs are now
// invoked per cycle as of W5 chore #6 (Anomaly Detector wired).
export const DISCOVERY_UNITS: readonly DiscoveryUnitEntry[] = [
  { name: 'Pattern Scanner', state: 'running' },
  { name: 'Combination Lab', state: 'running' },
  { name: 'Folding Lab', state: 'running' },
  { name: 'Stress Tester', state: 'running' },
  { name: 'Naming Authority', state: 'running' },
  { name: 'Anomaly Detector', state: 'running' },
];

export function discoveryUnitState(name: string): DiscoveryUnitState | undefined {
  return DISCOVERY_UNITS.find((u) => u.name === name)?.state;
}

function BrainSpecificSection({
  brainId,
  logEntries,
}: {
  brainId: string;
  logEntries: OmegaLogEntry[];
}) {
  switch (brainId) {
    case 'knowledge':
      return (
        <BrainLogSection
          title="Recent Market Research"
          entries={logEntries
            .filter(
              (e) =>
                e.entry_type.toLowerCase().includes('market_research') ||
                e.entry_type.toLowerCase().includes('research_report'),
            )
            .slice(-10)}
          emptyLabel="market research"
        />
      );

    case 'analytics':
      return (
        <BrainLogSection
          title="Recent Gap Findings"
          entries={logEntries
            .filter(
              (e) =>
                e.entry_type.toLowerCase().includes('gap') ||
                e.entry_type.toLowerCase().includes('analytic'),
            )
            .slice(-10)}
          emptyLabel="analytics"
        />
      );

    case 'production':
      return (
        <BrainLogSection
          title="Recent Product Bundles"
          entries={logEntries
            .filter(
              (e) =>
                e.entry_type.toLowerCase().includes('product') ||
                e.entry_type.toLowerCase().includes('bundle') ||
                e.entry_type.toLowerCase().includes('publish'),
            )
            .slice(-10)}
          emptyLabel="product bundle"
        />
      );

    case 'defense':
      return (
        <BrainLogSection
          title="Recent Monitor Reports"
          entries={logEntries
            .filter(
              (e) =>
                e.entry_type.toLowerCase().includes('monitor') ||
                e.entry_type.toLowerCase().includes('threat') ||
                e.entry_type.toLowerCase().includes('defense'),
            )
            .slice(-20)}
          emptyLabel="monitor report"
          showErrorRate
        />
      );

    case 'discovery':
      return <DiscoveryUnitsSection />;

    case 'decision':
      return (
        <BrainLogSection
          title="Recent Decisions"
          entries={logEntries
            .filter((e) => e.entry_type.toLowerCase().includes('decision'))
            .slice(-20)}
          emptyLabel="decision"
          widePayload
        />
      );

    default:
      return null;
  }
}

function BrainLogSection({
  title,
  entries,
  emptyLabel,
  showErrorRate = false,
  widePayload = false,
}: {
  title: string;
  entries: OmegaLogEntry[];
  emptyLabel: string;
  showErrorRate?: boolean;
  widePayload?: boolean;
}) {
  const [drawerEntry, setDrawerEntry] = useState<OmegaLogEntry | null>(null);

  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <h2 className="mb-5 text-lg font-semibold text-ink">{title}</h2>

      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">
          No {emptyLabel} entries recorded yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                <th className="px-3 py-2 text-start font-medium">Seq</th>
                <th className="px-3 py-2 text-start font-medium">Entry Type</th>
                {showErrorRate && (
                  <th className="px-3 py-2 text-start font-medium">Error</th>
                )}
                <th className="px-3 py-2 text-start font-medium">Payload</th>
                <th className="px-3 py-2 text-end font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const p = e.payload_json as Record<string, unknown> | null;
                const payloadPreview = p !== null
                  ? JSON.stringify(p).slice(0, widePayload ? 80 : 60)
                  : '—';
                const hasError =
                  p !== null &&
                  typeof p === 'object' &&
                  'error' in p &&
                  p['error'] !== null &&
                  p['error'] !== undefined;

                return (
                  <tr key={e.seq} className="border-t border-border hover:bg-surface-raised/50">
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted">#{e.seq}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink ltr-content">
                      {e.entry_type}
                    </td>
                    {showErrorRate && (
                      <td className="px-3 py-2">
                        {hasError ? (
                          <span className="inline-block h-2 w-2 rounded-full bg-warning" title="Error present" />
                        ) : (
                          <span className="inline-block h-2 w-2 rounded-full bg-success" title="No error" />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted ltr-content max-w-[300px] truncate">
                      {payloadPreview}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <button
                        onClick={() => setDrawerEntry(e)}
                        className="rounded border border-border px-2 py-1 text-xs text-ink-muted hover:border-spark hover:text-spark"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawerEntry && (
        <ExecDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />
      )}
    </section>
  );
}

export default OmegaBrainPage;
